import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { runGqlQueryV2 } from "../../../../../../graphql/client";
import { gqlClient, gqlHeader } from "../../../../../../graphql/client";
import {
  PlanTypeLiterals,
  ProductCodeTypeLiterals,
} from "../../../../../../application/domain/organization/org-product";
import {
  ProductCodeTargetTypeMap,
  ProductDataRemovableProductCodeLiterals,
  ProductDataRemovalTask,
  toProductDataRemovalTask,
  toProductDataRemovalTaskRequest,
} from "../../../../../../application/domain/product-data-removal-task";
import apiClient from "../../../../../../application/apis/api-client";
import { OrganizationEntity } from "../../../../../../application/domain/organization/organization";
import { SmartApplyApiError } from "@graffer-inc/smart-apply";

type OrgProduct = {
  productCode: ProductCodeTypeLiterals;
  plan: PlanTypeLiterals;
};

/*
 * Queries
 */
export const useFetchOrgData = (orgId: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<OrganizationEntity>();

  const fetchOrgData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiClient.getOrganization(orgId);
      setData(res.data);
    } catch (e) {
      toast.error("テナント情報の取得に失敗しました。");
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchOrgData();
  }, [fetchOrgData]);

  return {
    data,
    isLoading,
    refetch: fetchOrgData,
  };
};

export const useFetchOrgProducts = (orgId: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<OrgProduct[]>([]);

  const fetchOrgProducts = useCallback(async () => {
    setIsLoading(true);
    const res = await runGqlQueryV2(() =>
      gqlClient.allOrgProductsByOrgId({ orgId }, gqlHeader("q")),
    );
    if (res.error) {
      toast.error("製品情報の取得に失敗しました。");
      return;
    }
    const orgProducts = res.data?.allOrgProductsByOrgId.map((p) => ({
      productCode: p.productCode,
      plan: p.plan,
    }));
    setData(orgProducts ?? []);
    setIsLoading(false);
  }, [orgId]);

  useEffect(() => {
    fetchOrgProducts();
  }, [fetchOrgProducts]);

  return {
    data,
    isLoading,
    refetch: fetchOrgProducts,
  };
};

export const useFetchProductDataRemovalTasks = (
  orgId: string,
  productCode: ProductDataRemovableProductCodeLiterals,
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<ProductDataRemovalTask[]>([]);

  const fetchProductDataRemovalTasks = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiClient.fetchProductDataRemovalTasks(
        orgId,
        ProductCodeTargetTypeMap[productCode],
      );
      setData(res.data.map(toProductDataRemovalTask) ?? []);
    } catch (e) {
      toast.error("製品データ削除タスクの取得に失敗しました。");
    } finally {
      setIsLoading(false);
    }
  }, [orgId, productCode]);

  useEffect(() => {
    fetchProductDataRemovalTasks();
  }, [fetchProductDataRemovalTasks]);

  return {
    data,
    isLoading,
    refetch: fetchProductDataRemovalTasks,
  };
};

/*
 * Mutations
 */
export const useCreateProductDataRemovalTask = () => {
  const [isMutating, setIsMutating] = useState(false);

  const trigger = useCallback(
    async (
      orgId: string,
      productCode: ProductDataRemovableProductCodeLiterals,
      body: Pick<ProductDataRemovalTask, "dueAt">,
    ) => {
      setIsMutating(true);
      let response: ProductDataRemovalTask | undefined;
      try {
        const res = await apiClient.createProductDataRemovalTask(
          orgId,
          ProductCodeTargetTypeMap[productCode],
          toProductDataRemovalTaskRequest(body),
        );
        response = toProductDataRemovalTask(res.data);
      } catch (e) {
        toast.error("製品データ削除タスクの作成に失敗しました。");
      } finally {
        setIsMutating(false);
      }
      return response;
    },
    [],
  );

  return {
    isMutating,
    trigger,
  };
};

export const useApproveProductDataRemovalTask = () => {
  const [isMutating, setIsMutating] = useState(false);

  const trigger = useCallback(async (task: ProductDataRemovalTask) => {
    setIsMutating(true);
    let response: ProductDataRemovalTask | undefined;
    try {
      const res = await apiClient.approveProductDataRemovalTask(
        task.orgId,
        task.targetType,
        task.id,
      );
      response = toProductDataRemovalTask(res.data);
    } catch (e) {
      if (SmartApplyApiError.is403(e)) {
        toast.error("作成者自身が承認することはできません。");
        return;
      } else {
        toast.error("製品データ削除タスクの承認に失敗しました。");
      }
    } finally {
      setIsMutating(false);
    }
    return response;
  }, []);

  return {
    isMutating,
    trigger,
  };
};

export const useCancelProductDataRemovalTask = () => {
  const [isMutating, setIsMutating] = useState(false);

  const trigger = useCallback(async (task: ProductDataRemovalTask) => {
    setIsMutating(true);
    let response: ProductDataRemovalTask | undefined;
    try {
      const res = await apiClient.cancelProductDataRemovalTask(
        task.orgId,
        task.targetType,
        task.id,
      );
      response = toProductDataRemovalTask(res.data);
    } catch (e) {
      toast.error("製品データ削除タスクのキャンセルに失敗しました。");
    } finally {
      setIsMutating(false);
    }
    return response;
  }, []);

  return {
    isMutating,
    trigger,
  };
};
